/**
 * useI18nInitializer - Updates i18n config with database locales
 * 
 * Call this once in App.tsx after the app mounts.
 * i18next starts with bootstrap config, then updates dynamically.
 * 
 * Source of Truth: system_locale_configs table
 * - Locales from is_enabled
 * - Fallbacks from primary_locale
 * - RTL detection from is_rtl column
 */

import { useEffect, useRef } from 'react';
import i18n from 'i18next';
import { useLocaleConfigs } from './useLocaleConfigs';
import { 
  buildSupportedLocales, 
  buildFallbackConfig, 
  createRtlChecker 
} from '@/config/localeConfig';
import { resolveCanonicalLocale } from '@/lib/localeResolver';
import { setRuntimeRtlChecker } from '@/lib/runtimeRtlChecker';
import { logger } from '@/utils/logger';
import { isOnTributarioDomain } from '@/constants/branding';

/**
 * Hook to initialize i18n with database locale configs
 * Updates i18next options at runtime after database loads
 */
export function useI18nInitializer() {
  const { data: configs, isSuccess, isLoading } = useLocaleConfigs();
  const initialized = useRef(false);

  useEffect(() => {
    if (isSuccess && configs && configs.length > 0 && !initialized.current) {
      const supportedLngs = buildSupportedLocales(configs);
      const fallbackLng = buildFallbackConfig(configs);
      
      // Update i18next options at runtime (now that DB is loaded)
      // NOTE: supportedLngs was permissive (false) during bootstrap
      // Now we set the real list from database
      if (i18n.options) {
        i18n.options.supportedLngs = supportedLngs;
        i18n.options.fallbackLng = fallbackLng;
      }
      
      // Create runtime RTL checker from database is_rtl column
      const rtlChecker = createRtlChecker(configs);
      setRuntimeRtlChecker(rtlChecker);
      
      // Re-apply RTL detection for current language
      const currentLng = i18n.language;
      if (currentLng && rtlChecker) {
        document.documentElement.dir = rtlChecker(currentLng) ? 'rtl' : 'ltr';
      }
      
      // Re-apply stored locale - use resolveCanonicalLocale for consistency
      // On tributario.com.br, skip re-application of non-PT locales to prevent
      // stale localStorage from overriding the domain-enforced pt-BR
      const storedLocale = localStorage.getItem('user-locale');
      if (storedLocale && storedLocale !== currentLng) {
        // On tributario, don't re-apply non-PT locales — they are stale cross-domain artifacts
        if (isOnTributarioDomain() && !storedLocale.startsWith('pt')) {
          logger.info('[i18n] Tributario: skipping stale non-PT stored locale', { storedLocale, currentLng });
        } else {
          // Use the canonical resolver to normalize the stored locale
          const targetLocale = resolveCanonicalLocale(
            storedLocale, 
            supportedLngs, 
            typeof navigator !== 'undefined' ? navigator.language : undefined
          );
          
          // Update localStorage with canonical locale if it changed
          if (targetLocale !== storedLocale) {
            localStorage.setItem('user-locale', targetLocale);
            logger.info('[i18n] Normalized stored locale:', { storedLocale, targetLocale });
          }
          
          if (targetLocale !== currentLng) {
            logger.info('[i18n] Applying stored locale:', { storedLocale, targetLocale, currentLng });
            i18n.changeLanguage(targetLocale);
          }
        }
      }
      
      // On tributario.com.br, PT is the default but users can switch language
      // (branding updates dynamically when locale changes)

      initialized.current = true;
      logger.info('[i18n] Updated config from database', { 
        localeCount: supportedLngs.length,
        rtlCount: configs.filter(c => c.isRtl).length,
        currentLng: i18n.language,
      });
    }
  }, [isSuccess, configs]);

  return { 
    isReady: initialized.current || isSuccess,
    isLoading,
    localeCount: configs?.length ?? 0,
  };
}
